import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useNavigate, useParams } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { _get } from "../services/api.service";
import type { LibraryTopic } from "./library/library.data";
import MatchPickerDialog from "./library/match-picker-dialog";
import "./content-engine-v2/content-engine.css";
import "./video-create/CreateVideo.css";
import "./video-projects/VideoProjects.css";
import "./library/Library.css";

const PER_PAGE = 15;

interface MatchedBlock {
  block_id: number;
  content_id: number;
  block_type?: string;
  block_type_label?: string;
  title: string;
  caption: string;
  content_format?: string | null;
}

interface SearchResult extends LibraryTopic {
  matched_blocks?: MatchedBlock[];
}

interface SearchResponse {
  count: number;
  results: SearchResult[];
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function MatchSnippet({ text, query }: { text: string; query: string }) {
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="lib-match-highlight">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function MatchContext({ topic, query }: { topic: SearchResult; query: string }) {
  const blocks = topic.matched_blocks ?? [];
  if (blocks.length === 0) {
    return (
      <div className="lib-match-title-pill">
        <TagOutlinedIcon sx={{ fontSize: 12 }} />
        <span>Matched in title</span>
      </div>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1.2, mb: 0.5 }}>
      {blocks.slice(0, 2).map((block) => {
        const matchedInTitle = block.title && query && block.title.toLowerCase().includes(query.toLowerCase());
        const snippet = matchedInTitle ? block.title : block.caption;
        return (
          <div key={block.block_id} className="lib-match-block">
            <TextSnippetOutlinedIcon sx={{ fontSize: 12, mt: "2px", color: "#2364e8", flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <div className="lib-match-block-label">
                {block.block_type_label || block.title || "Script"}
              </div>
              {snippet && (
                <div className="lib-match-block-snippet">
                  <MatchSnippet text={snippet} query={query} />
                </div>
              )}
            </Box>
          </div>
        );
      })}
      {blocks.length > 2 && (
        <span className="lib-match-more">
          +{blocks.length - 2} more matches in scripts
        </span>
      )}
    </Box>
  );
}

function TopicCard({ topic, index, query, onShowMatches }: { topic: SearchResult; index: number; query?: string; onShowMatches?: (topic: SearchResult) => void }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    const matched = topic.matched_blocks ?? [];
    if (query && matched.length > 0 && onShowMatches) {
      onShowMatches(topic);
      return;
    }
    navigate(`/library/topic/${topic.topic_id}`);
  };

  return (
    <div
      className={`lib-topic-card${hovered ? " is-hovered" : ""}`}
      style={{ animationDelay: `${Math.min(index % PER_PAGE, 5) * 0.06}s` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {topic.video_created && (
        <Tooltip title="Ready to create video">
          <span className="lib-topic-video-badge">
            <VideocamIcon sx={{ fontSize: 16 }} />
          </span>
        </Tooltip>
      )}
      <div className="lib-topic-title">
        {query ? <MatchSnippet text={capitalize(topic.title)} query={query} /> : capitalize(topic.title)}
      </div>

      {query && <MatchContext topic={topic} query={query} />}

      <div className="lib-topic-meta">
        <span className="lib-topic-meta-row">
          <ArticleOutlinedIcon sx={{ fontSize: 12 }} />
          {topic.content_count} {topic.content_count === 1 ? "Script" : "Scripts"}
        </span>

        <span>&middot;</span>

        <span className="lib-topic-meta-row">
          {topic.total_versions} {topic.total_versions === 1 ? "Version" : "Versions"}
        </span>

        <span className="lib-topic-updated">
          <ScheduleIcon sx={{ fontSize: 11 }} />
          Updated {relativeTime(topic.last_updated)}
        </span>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="lib-skeleton-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="lib-skeleton-card">
          <Skeleton variant="text" width="65%" sx={{ fontSize: 15, mb: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width={50} sx={{ fontSize: 11 }} />
            </Box>
            <Skeleton variant="text" width={8} sx={{ fontSize: 10 }} />
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
              <Skeleton variant="text" width={60} sx={{ fontSize: 11 }} />
            </Box>
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, mt: 0.5 }}>
            <Skeleton variant="circular" width={11} height={11} />
            <Skeleton variant="text" width={80} sx={{ fontSize: 11 }} />
          </Box>
        </div>
      ))}
    </div>
  );
}

const SECTION_SLUG_MAP: Record<string, SectionLabel> = {
  today: "Today",
  yesterday: "Yesterday",
  "this-week": "This Week",
  older: "Older",
};

const SECTION_SUBTITLES: Record<SectionLabel, string> = {
  Today: "All topics created today",
  Yesterday: "All topics from yesterday",
  "This Week": "All topics from this week",
  Older: "Archive of older topics in your library",
};

type SectionLabel = "Today" | "Yesterday" | "This Week" | "Older";

const LibraryViewAll = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();
  const safeSection = SECTION_SLUG_MAP[section?.toLowerCase() ?? ""] ?? "Older";

  const [loading, setLoading] = useState(true);
  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(null);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [matchTopic, setMatchTopic] = useState<SearchResult | null>(null);
  const lastRequest = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (currentPage: number, currentSearch: string) => {
    const requestKey = `${safeSection}|${currentPage}|${currentSearch.trim()}`;
    if (lastRequest.current === requestKey) return;
    lastRequest.current = requestKey;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isInitial = currentPage === 1;
    setLoading(isInitial);
    setPageLoading(!isInitial);

    const params: Record<string, string | number> = {
      section: safeSection,
      page: currentPage,
      page_size: PER_PAGE,
    };
    if (currentSearch.trim()) {
      params.search = currentSearch.trim();
    }

    const startedAt = Date.now();
    const MIN_LOADING_MS = 300;
    try {
      const res = await _get("/content-engine/library/search/", { params, signal: controller.signal }, true) as SearchResponse;
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_LOADING_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
      }
      setSearchResponse(res);
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        setPageLoading(false);
      }
    }
  }, [safeSection]);

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed.length >= 3) {
      const timer = setTimeout(() => setDebouncedSearch(trimmed), 300);
      return () => clearTimeout(timer);
    }
    setDebouncedSearch("");
  }, [search]);

  useEffect(() => {
    setPage(1);
    fetchData(1, debouncedSearch);
  }, [safeSection, fetchData]);

  useEffect(() => {
    fetchData(page, debouncedSearch);
  }, [page, debouncedSearch, fetchData]);

  const totalPages = searchResponse ? Math.max(1, Math.ceil(searchResponse.count / PER_PAGE)) : 1;
  const safePage = Math.min(page, totalPages);
  const results = searchResponse?.results ?? [];

  const goToPage = (p: number) => {
    setPage(p);
  };

  return (
    <div className="container-fluid library-page video-projects-page">
      <MainHeading
        title={safeSection}
        subTitle={SECTION_SUBTITLES[safeSection]}
      />

      <div className="lib-layout vp-layout">
        <div className="lib-toolbar vp-toolbar">
          <div className="lib-view-all-count">
            <span className="lib-view-all-count-value">{searchResponse?.count ?? 0}</span>
            <span className="lib-view-all-count-label">
              Topic{searchResponse?.count === 1 ? "" : "s"}
            </span>
          </div>

          <div className="lib-toolbar-search">
            <TextField
              className="lib-search"
              placeholder="Search topics or scripts..."
              autoComplete="off"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setSearch(""); setPage(1); }
              }}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#66738a" }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearch(""); setPage(1); }} sx={{ p: 0.4, color: "#66738a", "&:hover": { color: "#16213a" } }}>
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {debouncedSearch.trim().length >= 3 && !loading && !pageLoading && (
              <span className="lib-search-status">
                {searchResponse?.count ?? 0} found
              </span>
            )}
          </div>

          <div className="vp-toolbar-actions">
            <Tooltip title="Back to Library">
              <button
                type="button"
                className="cv-btn cv-btn-secondary lib-back-btn"
                onClick={() => navigate("/library")}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
                Back to Library
              </button>
            </Tooltip>
          </div>
        </div>

        {loading || pageLoading ? (
          <SkeletonGrid />
        ) : results.length === 0 ? (
          <div className="vp-empty lib-fade-in">
            <div className="vp-empty-icon">
              <SearchOffOutlinedIcon sx={{ fontSize: 30 }} />
            </div>
            <h3 className="vp-empty-title">
              {debouncedSearch.trim() ? "No topics match your search" : "No topics in this section"}
            </h3>
            <p className="vp-empty-text">
              {debouncedSearch.trim()
                ? "Try different keywords or clear the search to see all topics in this section."
                : "New topics you create will appear here automatically."}
            </p>
          </div>
        ) : (
          <>
            <div className="lib-grid">
              {results.map((topic, index) => (
                <TopicCard
                  key={topic.topic_id}
                  topic={topic}
                  index={(safePage - 1) * PER_PAGE + index}
                  query={debouncedSearch}
                  onShowMatches={setMatchTopic}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="lib-pagination">
                <button
                  type="button"
                  className="lib-page-btn"
                  onClick={() => goToPage(Math.max(1, safePage - 1))}
                  disabled={safePage <= 1}
                >
                  <NavigateBeforeIcon sx={{ fontSize: 18 }} />
                  Previous
                </button>

                <span className="lib-page-indicator">
                  Page {safePage} of {totalPages}
                </span>

                <button
                  type="button"
                  className="lib-page-btn"
                  onClick={() => goToPage(Math.min(totalPages, safePage + 1))}
                  disabled={safePage >= totalPages}
                >
                  Next
                  <NavigateNextIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <MatchPickerDialog
        open={!!matchTopic}
        topic={matchTopic}
        query={debouncedSearch}
        onClose={() => setMatchTopic(null)}
      />
    </div>
  );
};

export default LibraryViewAll;
